import { isValid, parse, startOfDay } from 'date-fns'
import { memo, useRef, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form'
import {
  Platform,
  TextInput as RNTextInput,
  View,
  ViewStyle,
} from 'react-native'
import DateTimePickerModal from 'react-native-modal-datetime-picker'
import {
  TextInput as PaperTextInput,
  Portal,
  TextInputProps,
} from 'react-native-paper'
import DateTimePicker from 'react-native-ui-datepicker'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import { getErrorField } from '@/components/form/formUtils'
import TextInput from '@/components/form/TextInput'
import Skeleton from '@/components/shared/Skeleton'
import UniHelperText from '@/components/unistyles/UniHelperText'
import UniModal from '@/components/unistyles/UniModal'

interface Props<C extends FieldValues, E extends FieldErrors> {
  fieldName: Path<C>
  required: boolean
  errors?: E
  control: Control<C, any>
  isLoading?: boolean
  onBlur?: () => void
  disabled?: boolean
}

const FormFieldDate = <C extends FieldValues, E extends FieldErrors>({
  fieldName,
  required,
  errors,
  control,
  style,
  isLoading,
  onBlur,
  disabled,
  ...props
}: Props<C, E> & TextInputProps) => {
  const [showDatePicker, setShowDatePicker] = useState(false)
  const { theme } = useUnistyles()
  const inputRef = useRef<RNTextInput>(null)

  return (
    <View style={[style as ViewStyle, styles.root]}>
      <Skeleton isLoading={isLoading}>
        <Controller
          control={control}
          name={fieldName}
          render={({ field: { onChange, value } }) => (
            <View>
              <TextInput
                // @ts-expect-error label is not a valid prop for TextInput
                label={props.label?.toString()}
                testID={`${fieldName}-dateInput`}
                {...props}
                editable={!disabled}
                error={!!getErrorField(fieldName, errors)}
                onBlur={() => {
                  if ((value as unknown as Date) instanceof Date) {
                    onChange(startOfDay(value))
                  } else if (typeof value === 'string') {
                    const text = value?.trim()

                    if (text === '') {
                      onChange(null)
                      return
                    }
                    const date = parseFlexibleDate(text)
                    if (date) {
                      onChange(startOfDay(date))
                    } else {
                      onChange(text)
                    }
                  }

                  onBlur?.()
                }}
                onChangeText={onChange}
                // ref={inputRef}
                rightIcon={
                  <PaperTextInput.Icon
                    color={theme.colors.secondary}
                    icon="calendar"
                    onPress={() => {
                      if (!disabled) {
                        setShowDatePicker(true)
                      }
                    }}
                    testID={`calendar-icon-${fieldName}`}
                  />
                }
                submitBehavior="blurAndSubmit"
                value={displayValue(value)}
              />
              {Platform.OS === 'web' && showDatePicker && (
                <Portal>
                  <UniModal
                    contentContainerStyle={styles.webCalendarContainer}
                    onDismiss={() => setShowDatePicker(false)}
                    visible={showDatePicker}
                  >
                    <View style={styles.webCalendarView}>
                      <DateTimePicker
                        date={value || new Date()}
                        mode="single"
                        onChange={(params) => {
                          if (params.date) {
                            setShowDatePicker(false)
                            onChange(new Date(params.date.toLocaleString()))
                            onBlur?.()
                            inputRef.current?.blur()
                          }
                        }}
                      />
                    </View>
                  </UniModal>
                </Portal>
              )}
              {(Platform.OS === 'ios' || Platform.OS === 'android') && (
                <DateTimePickerModal
                  date={(value as any) instanceof Date ? value : new Date()}
                  isVisible={showDatePicker}
                  mode="date"
                  onCancel={() => setShowDatePicker(false)}
                  onConfirm={(date) => {
                    onChange(date)
                    setShowDatePicker(false)
                    inputRef.current?.blur()
                  }}
                />
              )}
              {getErrorField(fieldName, errors) && (
                <UniHelperText
                  testID={`${fieldName}-error`}
                  type="error"
                  visible={!!getErrorField(fieldName, errors)}
                >
                  {getErrorField(fieldName, errors)?.message as string}
                </UniHelperText>
              )}
            </View>
          )}
          rules={{
            required: required,
          }}
        />
      </Skeleton>
    </View>
  )
}

function displayValue(value: Date | null) {
  if (!value) return ''

  try {
    return value.toLocaleDateString()
  } catch {
    return value.toString()
  }
}

const styles = StyleSheet.create((theme) => ({
  root: {
    marginBottom: theme.tokens.spacing[2],
    minWidth: 150,
  },
  webCalendarContainer: {
    alignSelf: 'center',
    width: '45%',
  },
  webCalendarView: {
    backgroundColor: theme.colors.surface,
    flex: 1,
    padding: 10,
  },
}))

function parseFlexibleDate(dateString: string): Date | null {
  const formats = [
    'MM/dd/yy',
    'MM-dd-yy',
    'MM/dd/yyyy',
    'MM-dd-yyyy',
    'dd/MM/yy',
    'dd-MM-yy',
    'yyyy-MM-dd',
    'dd-MM-yyyy',
    'dd/MM/yyyy',
    'yyyy/MM/dd',
    'dd.MM.yyyy',
    'MM.dd.yyyy',
    'MMMM d, yyyy',
    'd MMMM yyyy',
    'yyyy-MM-dd HH:mm:ss',
    'dd-MM-yyyy HH:mm:ss',
  ]

  for (const format of formats) {
    const date = parse(dateString, format, new Date())
    if (isValid(date)) {
      return date
    }
  }

  return null
}

export default memo(FormFieldDate) as typeof FormFieldDate
